import { captureException } from '@sentry/react';
import { toast } from '@/shared/components/toasts/PlatformToast';

interface WorkspaceResourceState {
    archived: boolean;
    favorited: boolean;
    label: string;
    locked: boolean;
}

interface WorkspaceResourceStateAdapter {
    setArchived: (archived: boolean) => Promise<unknown>;
    setFavorite: (favorited: boolean) => Promise<unknown>;
    setLocked: (locked: boolean) => Promise<unknown>;
}

interface WorkspaceResourceStateActionCopy {
    archived: string;
    favorited: string;
    locked: string;
}

/**
 * Shared orchestration for the favorite, lock, and archive controls rendered in
 * resource headers and action menus. Persistence remains resource-specific;
 * success/error behavior and state transitions do not.
 */
export const useWorkspaceResourceStateActions = (
    resource: WorkspaceResourceState | undefined,
    adapter: WorkspaceResourceStateAdapter,
    copy: WorkspaceResourceStateActionCopy,
) => {
    const run = async (operation: () => Promise<unknown>, successMessage: string) => {
        if (!resource) {
            return;
        }

        try {
            await operation();
            toast(successMessage, { description: resource.label });
        } catch (error) {
            captureException(error);
            toast.error(`Unable to update ${resource.label}`);
        }
    };

    return {
        toggleArchive: () =>
            run(
                () => adapter.setArchived(!resource?.archived),
                resource?.archived ? `Removed from ${copy.archived}` : `Added to ${copy.archived}`,
            ),
        toggleFavorite: () =>
            run(
                () => adapter.setFavorite(!resource?.favorited),
                resource?.favorited ? `Removed from ${copy.favorited}` : `Added to ${copy.favorited}`,
            ),
        toggleLock: () =>
            run(
                () => adapter.setLocked(!resource?.locked),
                resource?.locked ? `Removed from ${copy.locked}` : `Added to ${copy.locked}`,
            ),
    };
};
